/**
 * 우측 패널 (디자인)
 * - 선택 없음: "파이프라인 요약" — 지표 4칸 · 상태별 노드 수 · 공장별 요약 · 상위 문제 노드
 * - 노드 선택: 노드 상세 — 상태 설명 · 지표 4칸(+정합률) · 마지막 실행 · 처리량 추이(최근 60점) · 부가정보
 * 값은 store 스냅샷 그대로이고, 측정하지 않는 값(null)은 — 로 보인다.
 */
import { memo, useMemo } from "react";
import { ThroughputChart } from "../metrics/ThroughputChart";
import { FACTORIES } from "../../data/machines";
import { STATUS_LABEL_KO, formatInt, formatLatency, formatLocalDateTime, formatNumber, formatTimeTooltip, } from "../../lib/format";
import { selectActions, selectNodeHistory, selectNodes, selectSelectedNode, selectSelectedNodeId, selectSummary, selectMode, usePipelineStore, } from "../../store/pipeline-store";
import { STATUS_ORDER } from "../flow/flow-constants";
import { statusLabel } from "../flow/PipelineNode";

const ISO_RE = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}/;
function extraText(v) {
    if (v == null || v === "")
        return "—";
    if (typeof v === "number")
        return Number.isInteger(v) ? formatInt(v) : formatNumber(v);
    if (ISO_RE.test(v))
        return formatLocalDateTime(v);
    return String(v);
}

/** 지표 칸: 왼쪽 위 라벨, 오른쪽 아래 큰 값 + 단위 */
function Tile({ label, value, unit, tone, title, wide }) {
    return (<div className={`dfv-tile${wide ? " dfv-tile--wide" : ""}`} title={title ?? label}>
      <span className="dfv-tile__label">{label}</span>
      <span className="dfv-tile__value">
        <b className={tone ? tone : undefined}>{value}</b>
        {unit && <small>{unit}</small>}
      </span>
    </div>);
}

/** "1.2초" 같은 지연을 값/단위로 나눈다 (디자인은 숫자 + 작은 s) */
function latencyParts(sec) {
    if (sec == null || !Number.isFinite(sec) || sec < 0)
        return ["—", null];
    if (sec < 60)
        return [formatNumber(sec, 0), "s"];
    return [formatLatency(sec), null];
}

function NodeDetail({ node, onClose }) {
    const d = node.data;
    const history = usePipelineStore(useMemo(() => selectNodeHistory(node.id), [node.id]));
    const extra = useMemo(() => Object.entries(d.extra ?? {}), [d.extra]);
    // 설명은 "상태 — 세부" 형태가 많다 → 왼쪽 상태 / 오른쪽 세부로 나눠 보인다
    const [descHead, descTail] = (() => {
        const parts = (d.description || "").split(" — ");
        return parts.length > 1 ? [parts[0], parts.slice(1).join(" — ")] : [statusLabel(d.status, d.nodeType), d.description];
    })();
    const factory = d.factoryId ? FACTORIES.find((f) => f.id === d.factoryId) : null;
    const title = d.nodeType === "source" && factory ? `${d.label}` : d.label;
    const [latV, latU] = latencyParts(d.latency);
    const tpUnit = d.nodeType === "source" ? "Shots/Min" : "Rows/Min";

    return (<div className="dfv-detail">
      <div className="dfv-detail__head">
        <h2 className="dfv-panel__title" title={node.id}>{title}</h2>
        <button type="button" className="dfv-icon-btn" aria-label="상세 닫기 (Esc)" title="상세 닫기 (Esc)" onClick={onClose}>
          <span className="material-symbols-outlined" aria-hidden="true">close</span>
        </button>
      </div>
      <div className="dfv-detail__status" data-status={d.status}>
        <span className="dfv-detail__state">{descHead}</span>
        <span className="dfv-detail__desc" title={d.description}>{descTail}</span>
      </div>

      <div className="dfv-tiles">
        <Tile label="총 처리량" value={formatNumber(d.throughput, d.throughput >= 100 ? 0 : 1)} unit={tpUnit} title={d.nodeType === "source" ? "최근 10분 샷 수 기준 분당 평균" : "분당 처리 행 수"}/>
        <Tile label="지연" value={latV} unit={latU} title={d.nodeType === "source" ? "마지막 샷 도착 후 경과" : "마지막 도착·동기화 후 경과"}/>
        <Tile label="성공률" value={d.successRate == null ? "—" : formatNumber(d.successRate, 1)} unit={d.successRate == null ? null : "%"} title={d.successRate == null ? "미측정" : "성공 비율"}/>
        <Tile label="재시도" value={d.retryCount == null ? "—" : formatInt(d.retryCount)} unit="회" title={d.retryCount == null ? "미측정" : "재시도 횟수"}/>
        {d.factoryId && (<Tile wide label="정합률 (공정/비전)" value={d.consistencyRate == null ? "—" : formatNumber(d.consistencyRate, 1)} unit={d.consistencyRate == null ? null : "%"} title={d.consistencyRate == null ? "비전 미연동 — 매칭할 비전 기록 없음" : `최근 24h 공정 샷 ${formatInt(d.consistencySamples)}건 중 UUID 로 비전 기록이 매칭된 비율`}/>)}
      </div>

      <div className="dfv-detail__row">
        <span><span className="material-symbols-outlined" aria-hidden="true">schedule</span>마지막 실행</span>
        <b title={formatTimeTooltip(d.lastUpdated)}>{formatLocalDateTime(d.lastUpdated)}</b>
      </div>

      <div className="dfv-section-head">
        <h3>처리량 추이</h3>
        <span>최근 60점</span>
      </div>
      <div className="dfv-chart">
        <ThroughputChart history={history} nodeType={d.nodeType} status={d.status} height={150}/>
      </div>

      {extra.length > 0 && (<>
          <div className="dfv-section-head"><h3>부가정보</h3></div>
          <table className="dfv-kv">
            <tbody>
              {extra.map(([k, v]) => {
                    const text = extraText(v);
                    return (<tr key={k}>
                    <th scope="row" title={k}>{k}</th>
                    <td title={text}>{text}</td>
                  </tr>);
                })}
            </tbody>
          </table>
        </>)}
    </div>);
}

function PipelineSummaryView() {
    const nodes = usePipelineStore(selectNodes);
    const summary = usePipelineStore(selectSummary);
    const mode = usePipelineStore(selectMode);
    const { requestFocusNode } = usePipelineStore(selectActions);
    const has = mode !== "idle" || nodes.length > 0;

    const byStatus = useMemo(() => {
        const m = { healthy: 0, running: 0, warning: 0, error: 0, recovering: 0, offline: 0 };
        for (const n of nodes)
            m[n.data.status]++;
        return m;
    }, [nodes]);

    const factories = useMemo(() => FACTORIES.map((f) => {
        const machines = nodes.filter((n) => n.data.nodeType === "source" && n.data.factoryId === f.id);
        const stage = (kind) => nodes.find((n) => n.id === `${kind}:${f.id}`);
        const ok = machines.filter((n) => n.data.status === "running" || n.data.status === "healthy").length;
        const bad = machines.filter((n) => n.data.status === "error" || n.data.status === "warning").length;
        const stopped = machines.filter((n) => n.data.status === "offline").length;
        const throughput = machines.reduce((a, n) => a + (Number.isFinite(n.data.throughput) ? n.data.throughput : 0), 0);
        const withCons = machines.filter((n) => n.data.consistencyRate != null && n.data.consistencySamples > 0);
        const samples = withCons.reduce((a, n) => a + n.data.consistencySamples, 0);
        const consistency = samples > 0 ? Math.round(withCons.reduce((a, n) => a + n.data.consistencyRate * n.data.consistencySamples, 0) / samples * 10) / 10 : null;
        return { f, total: machines.length, ok, bad, stopped, throughput, consistency, stages: [["Collector", stage("collector")], ["Outbox", stage("outbox")], ["Uploader", stage("uploader")]] };
    }), [nodes]);

    const problems = useMemo(() => nodes
        .filter((n) => n.data.status === "error" || n.data.status === "warning")
        .sort((a, b) => (a.data.status === b.data.status ? 0 : a.data.status === "error" ? -1 : 1))
        .slice(0, 8), [nodes]);

    const [avgV, avgU] = latencyParts(summary.avgLatency);
    return (<div className="dfv-summary">
      <h2 className="dfv-panel__title">파이프라인 요약</h2>
      <div className="dfv-tiles">
        <Tile label="총 처리량" value={has ? formatNumber(summary.totalThroughput, summary.totalThroughput >= 100 ? 0 : 1) : "—"} unit="Shots/Min" title="전체 설비 처리량 합계"/>
        <Tile label="평균 지연" value={has ? avgV : "—"} unit={has ? avgU : null} title="공장별 마지막 샷 도착 후 경과 평균"/>
        <Tile label="오류율" value={has ? formatNumber(summary.errorRate, 1) : "—"} unit="%" tone={summary.errorRate > 0 ? "is-bad" : undefined} title="오류 상태 노드 비율"/>
        <Tile label="노드 수" value={has ? formatInt(nodes.length) : "—"} unit="개"/>
      </div>

      <div className="dfv-section-head"><h3>상태별 노드수</h3></div>
      <ul className="dfv-status-grid">
        {STATUS_ORDER.map((s) => (<li key={s} className="dfv-status-chip" data-status={s}>
            <i aria-hidden="true"/>
            <span>{STATUS_LABEL_KO[s]}</span>
            <b>{has ? byStatus[s] : "—"}</b>
          </li>))}
      </ul>

      <div className="dfv-section-head"><h3>공장별 요약</h3></div>
      <div className="dfv-factory-list">
        {factories.map(({ f, total, ok, bad, stopped, throughput, consistency, stages }) => (<div key={f.id} className="dfv-factory">
            <div className="dfv-factory__head">
              <span className="material-symbols-outlined" aria-hidden="true">factory</span>
              <span className="dfv-factory__name">{f.name} {total}대</span>
              <span className="dfv-factory__tp"><b>{formatNumber(throughput, throughput >= 100 ? 0 : 1)}</b> Shots/Min</span>
            </div>
            <div className="dfv-factory__stats">
              <span className="is-good">정상 <b>{ok}</b></span>
              <span className="is-bad">오류 <b>{bad}</b></span>
              <span className="is-off">정지 <b>{stopped}</b></span>
              <span className="is-muted" title="공정·비전 데이터 정합률 (비전 연동 설비 기준)">정합률(공정/비전) <b>{consistency == null ? "-" : `${consistency}%`}</b></span>
            </div>
            <div className="dfv-factory__stages">
              {stages.map(([name, n]) => n && (<button key={name} type="button" className="dfv-stage" data-status={n.data.status} onClick={() => requestFocusNode(n.id)} title={`${n.data.label} · ${STATUS_LABEL_KO[n.data.status]}`}>
                  <i aria-hidden="true"/>{name}
                </button>))}
            </div>
          </div>))}
      </div>

      <div className="dfv-section-head"><h3>상위 문제 노드</h3><span>{problems.length}</span></div>
      {problems.length === 0 ? (<div className="dfv-empty is-good">문제 노드가 없습니다.</div>) : (<ul className="dfv-problems">
          {problems.map((n) => (<li key={n.id}>
              <button type="button" className="dfv-problem" data-status={n.data.status} onClick={() => requestFocusNode(n.id)} title={`${n.data.label} · ${STATUS_LABEL_KO[n.data.status]}`}>
                <i aria-hidden="true"/>
                <span className="material-symbols-outlined" aria-hidden="true">{n.data.nodeType === "source" ? "precision_manufacturing" : "settings"}</span>
                <span className="dfv-problem__name">{n.data.label}</span>
                <span className="dfv-problem__badge">{n.data.status === "error" ? "Error" : "Warning"}</span>
              </button>
            </li>))}
        </ul>)}
    </div>);
}

export const NodeDetailPanel = memo(function NodeDetailPanel() {
    const selectedId = usePipelineStore(selectSelectedNodeId);
    const node = usePipelineStore(selectSelectedNode);
    const { selectNode } = usePipelineStore(selectActions);
    const close = () => selectNode(null);
    return (<aside className="dfv-panel dfv-right dfv-scroll" aria-label={node ? "노드 상세" : "파이프라인 요약"}>
      {node ? <NodeDetail key={node.id} node={node} onClose={close}/>
            : selectedId ? (<div className="dfv-empty">선택한 노드({selectedId})를 찾을 수 없습니다. <button type="button" className="dfv-link" onClick={close}>선택 해제</button></div>)
                : <PipelineSummaryView />}
    </aside>);
});
export default NodeDetailPanel;
